import transformRotate from "@turf/transform-rotate";
import centroid from "@turf/centroid";
import { events, updateActions } from "@mapbox/mapbox-gl-draw/src/constants";

function getAngle(center, point) {
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  return Math.atan2(dx, dy) * (180 / Math.PI);
}

const RotateMode = {};

RotateMode.onSetup = function (opts) {
  const featureIds = Array.isArray(opts.featureIds) && opts.featureIds.length ? opts.featureIds : null;
  const hasMultiple = !!featureIds;

  if (hasMultiple) {
    const features = featureIds.map((id) => this.getFeature(id)).filter(Boolean);

    if (!features.length) {
      throw new Error("No features found to rotate");
    }

    const originalFeatures = features.map((f) => f.toGeoJSON());

    // Use a single shared pivot for all geometries so they rotate together
    const featureCollection = {
      type: "FeatureCollection",
      features: originalFeatures
    };
    const primaryCenter = centroid(featureCollection);

    this.updateUIClasses({ mouse: "move" });

    return {
      featureIds,
      originalFeatures,
      center: primaryCenter.geometry.coordinates,
      initialAngle: 0,
      isMultiple: true
    };
  }

  const featureId = opts.featureId;
  const feature = this.getFeature(featureId);

  if (!feature) {
    throw new Error("No feature found to rotate");
  }

  const originalFeature = feature.toGeoJSON();
  const featureCenter = centroid(originalFeature);

  this.updateUIClasses({ mouse: "move" });

  return {
    featureId,
    originalFeature,
    center: featureCenter.geometry.coordinates,
    initialAngle: 0,
    isMultiple: false
  };
};

RotateMode.onMouseDown = function (state, e) {
  e.originalEvent.preventDefault();
  state.initialAngle = getAngle(state.center, [e.lngLat.lng, e.lngLat.lat]);
  if (state.isMultiple) {
    state.originalFeatures = (state.featureIds || [])
      .map((id) => {
        const feature = this.getFeature(id);
        return feature ? feature.toGeoJSON() : null;
      })
      .filter(Boolean);
  } else {
    state.originalFeature = this.getFeature(state.featureId).toGeoJSON();
  }
  this.map.dragPan.disable();
};

RotateMode.onDrag = function (state, e) {
  const currentAngle = getAngle(state.center, [e.lngLat.lng, e.lngLat.lat]);
  const deltaAngle = currentAngle - state.initialAngle;
  if (state.isMultiple) {
    (state.featureIds || []).forEach((id, index) => {
      const original = state.originalFeatures?.[index];
      if (!original) return;

      const rotated = transformRotate(structuredClone(original), deltaAngle, {
        pivot: state.center
      });
      const feature = this.getFeature(id);
      if (feature) {
        feature.setCoordinates(rotated.geometry.coordinates);
      }
    });
  } else {
    const rotated = transformRotate(structuredClone(state.originalFeature), deltaAngle, {
      pivot: state.center
    });
    const feature = this.getFeature(state.featureId);
    feature.setCoordinates(rotated.geometry.coordinates);
  }
};

RotateMode.onMouseUp = function (state) {
  this.map.dragPan.enable();
  if (state.isMultiple) {
    const features = (state.featureIds || [])
      .map((id) => this.getFeature(id))
      .filter(Boolean)
      .map((f) => f.toGeoJSON());

    this.map.fire(events.UPDATE, {
      action: updateActions.CHANGE_COORDINATES,
      features
    });
  } else {
    this.map.fire(events.UPDATE, {
      action: updateActions.CHANGE_COORDINATES,
      features: [this.getFeature(state.featureId).toGeoJSON()]
    });
  }
};

RotateMode.toDisplayFeatures = function (state, geojson, display) {
  display(geojson);
};

RotateMode.onStop = function () {
  this.map.dragPan.enable();
};

RotateMode.onTouchStart = RotateMode.onMouseDown;
RotateMode.onTouchMove = RotateMode.onDrag;
RotateMode.onTouchEnd = RotateMode.onMouseUp;

export default RotateMode;
